import { Image } from '@graphcommerce/image'
import { TabContext, TabPanel } from '@mui/lab'
import { Box, Tab, Tabs, styled } from '@mui/material'
import { useRouter } from 'next/router'
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
// eslint-disable-next-line import/no-cycle
import missingImageIcon from '../../assets/images/missing-image-icon.svg'
// eslint-disable-next-line import/no-cycle
import useDetectDevice, { DetectDeviceProps } from '../../hooks/useDetectDevice'
import useWindowSize from '../../hooks/useWindowSize'
import { AiChatFab } from '../AI/AiChatFab'
import { LayoutQuery } from '../Layout'
import { Icon } from '../Layout/Icon'
import { globalContext } from '../NextUi/globalContext'
import { HeaderTabsMobile } from './HeaderTabsMobile'
import { MegaMenuSubHeaderTab } from './MegaMenuSubHeaderTab'
import { NewMegaMenuSubHeaderTab } from './NewMegaMenuSubHeaderTab'
import { SubCategoryTab } from './SubCategoryTab'
import { SubHeaderTabs } from './SubHeaderTabs'

interface StyledTabsProps {
  children?: React.ReactNode
  value: string
  onChange: (event: React.SyntheticEvent, newValue: string) => void
  variant?: 'standard' | 'scrollable' | 'fullWidth'
  scrollButtons?: boolean | 'auto'
}

export const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    scrollButtons={false}
    TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }}
  />
))(({ theme }) => ({
  color: '#000000',
  minHeight: 48,
  height: 48,
  fontWeight: 'bold',
  overflow: 'hidden',
  fontSize: 24,
  [theme.breakpoints.up('md')]: {
    minHeight: 48,
    height: 48,
  },

  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    height: 4,
  },
  '& .MuiTabs-indicatorSpan': {
    backgroundColor: 'none',
  },
}))

export interface StyledTabProps {
  label: string
  id: string
  value: string
  'aria-controls': string
  onHover?: () => void
  icon?: ReactElement<any>
  onClick?: () => void
}

export const StyledTab = styled((props: StyledTabProps) => (
  <Tab
    disableRipple
    {...props}
    onMouseEnter={props.onHover}
    icon={props.icon}
    onClick={() => props.onClick && props?.onClick()}
  />
))(({ theme }) => ({
  minWidth: 0,
  paddingRight: 0,
  paddingLeft: 0,
  marginRight: '15px',
  textAlign: 'center',
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: 17,
  color: '#000000',
  [theme.breakpoints.up('md')]: {
    fontSize: 20,
    marginRight: '30px',
  },
  '&.Mui-selected': {
    animationDuration: '1s',
    color: '#A95CFF',
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#A95CFF',
    animationDelay: '1s',
  },
}))
export interface ImageItem {
  url: string
  alt: string
  label?: string
}

export interface MegaMenuItem {
  name: string
  id: string | number | null
  image_url: string | null
  moq: string | null
  production_time: string | null
  free_shipping: boolean | null
  isCategory: boolean
  url: string | null
  children: MegaMenuItem[]
}

export interface CategoryGroup {
  [groupName: string]: MegaMenuItem[]
}

export interface CategoriesResponse {
  categories: CategoryGroup[]
}

export function NewMegaMenuHeaderTabs(
  props: Pick<LayoutQuery, 'menu'> & { closeModal?: () => void } & {
    value: string
    setValue: Dispatch<SetStateAction<string>>
  },
) {
  const { menu, closeModal, value, setValue } = props
  const [showMenu, setShowMenu] = useState(false)
  const { setBlurBackground, menuItems } = useContext(globalContext)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const { isMobile } = useWindowSize()
  const deviceDetails: DetectDeviceProps = useDetectDevice() || {}
  const isTablet = deviceDetails?.isTablet || deviceDetails?.isIPad
  const [menuPath, setMenuPath] = useState<MegaMenuItem[]>([])
  const [selectedMenuHasChildren, setSelectedMenuHasChildren] = useState<boolean>(true)
  const router = useRouter()

  // Close modal when path changes
  useLayoutEffect(() => {
    closeModal?.()
    setValue('')
  }, [router.asPath])

  const activeRootCategory = value !== '' ? menuItems[parseInt(value, 10)] : null
  const currentMenu =
    menuPath.length > 0
      ? menuPath[menuPath.length - 1].children
      : activeRootCategory?.children ?? []

  const canGoBack = menuPath.length > 0
  const [animateIn, setAnimateIn] = useState(false)

  const goDeeper = (item: MegaMenuItem) => {
    if (item.children?.length) {
      setMenuPath((prev) => [...prev, item])
    }
  }

  const goBack = () => {
    setMenuPath((prev) => prev.slice(0, -1))
  }

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }
  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  })

  const handleClickParentCategory = async (url: string) => {
    await router.push(`/${url}`)
    setShowMenu(false)
  }

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      setShowMenu(true)
    }, 500)
  }

  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setShowMenu(false)
  }

  useEffect(() => {
    if (showMenu) {
      setValue(value)
    } else {
      setValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenu])

  useEffect(() => {
    // eslint-disable-next-line no-unsafe-optional-chaining
    if (value?.length > 0 && showMenu && selectedMenuHasChildren) {
      setBlurBackground(true)
    } else {
      setBlurBackground(false)
    }
  }, [selectedMenuHasChildren, setBlurBackground, showMenu, value])

  useEffect(() => {
    setAnimateIn(false) // reset first
    const id = requestAnimationFrame(() => setAnimateIn(true)) // trigger next frame
    return () => cancelAnimationFrame(id)
  }, [menuPath])

  useEffect(() => {
    if (value === '*') {
      requestAnimationFrame(() => {
        setShowMenu(true)
        setValue('0')
        setMenuPath([])
      })
    }
  }, [setValue, value])

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className='bg-white flex w-full flex-col items-center'
    >
      <TabContext value={value}>
        <div
          className={`mx-auto flex h-[60px] w-full items-center justify-between sm:h-auto sm:border-none md:max-w-maxContentWidth ${
            isTablet ? 'md:ml-[36px] lg:ml-[36px]' : 'md:mx-0'
          } md:justify-between 2xl:px-0`}
        >
          <div className='xl:px-auto mx-5 hidden w-full items-center justify-between px-[5%] sm:block 2xl:mx-auto 2xl:px-0'>
            <StyledTabs
              value={value}
              onChange={handleChange}
              variant='standard'
              // scrollButtons='auto'
              sx={{
                width: '100%',
                display: { md: 'flex' },
                marginTop: 0,
                maxHeight: '90vh',
              }}
            >
              {menuItems?.map((category, index) => (
                <StyledTab
                  key={category?.url}
                  label={category.name ?? ''}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  onHover={() => {
                    setMenuPath([])
                    setValue(index.toString())
                    setSelectedMenuHasChildren(category?.children?.length > 0)
                  }}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  {...a11yProps(index)}
                  onClick={
                    category?.url ? () => handleClickParentCategory(category?.url ?? '') : () => {}
                  }
                  value={index.toString()}
                />
              ))}
            </StyledTabs>
          </div>
          <div className='mx-5 block w-full items-center justify-between lg:px-[5%] md:hidden'>
            <StyledTabs
              value={value}
              onChange={handleChange}
              variant='standard'
              scrollButtons='auto'
              sx={{
                width: '100%',
                display: 'flex',
                marginTop: 0,
                maxHeight: '90vh',
              }}
            >
              {menuItems?.map((category, index) => (
                <StyledTab
                  key={category?.url}
                  label={category.name ?? ''}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument

                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  {...a11yProps(index)}
                  onClick={() => {
                    if (category?.url) {
                      handleClickParentCategory(category?.url ?? '')
                    } else {
                      setMenuPath([])
                      setValue(index.toString())
                      setSelectedMenuHasChildren(category?.children?.length > 0)
                    }
                  }}
                  value={index.toString()}
                />
              ))}
            </StyledTabs>
            <button
              type='button'
              className='absolute right-[10px] top-[10px] mt-[6px] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-full'
              onClick={() => {
                closeModal?.()
                setValue('')
              }}
            >
              <Icon name='close' className='Type-XXXL-Bold text-tight-black' />
            </button>
          </div>
        </div>
        <div
          key={`${value}-${menuPath.length}`}
          className={`relative w-full ${showMenu ? 'block' : 'hidden'}`}
        >
          <div className='absolute left-0 top-0 z-10 h-full w-full border-t-[1px] border-t-20-grey bg-pure-white shadow-xl'>
            <NewMegaMenuSubHeaderTab
              menuItems={currentMenu}
              setShowMenu={setShowMenu}
              onSelect={goDeeper}
              onBack={menuPath.length > 0 ? goBack : undefined}
              showHeader
              animateIn={animateIn}
              label={menuPath.at(-1)?.name}
            />
          </div>
        </div>
      </TabContext>
    </div>
  )
}
